import { defineModule } from '../utils/helpers';

/**
 * `base64_encode()` encodes the address's UTF-8 bytes, and `atob()` hands those
 * bytes back one per character, so a non-ASCII address has to be read as UTF-8
 * again. Bytes that are not valid UTF-8 come back percent-encoded above 0x7F,
 * which the browser passes to the mail client as they are. Throws only on a
 * value that is not base64.
 */
export const decodeAddress = (encoded: string) => {
  const bytes = Uint8Array.from(atob(encoded), (char) => char.charCodeAt(0));

  try {
    // Without ignoreBOM a leading EF BB BF is dropped from the address.
    return new TextDecoder('utf-8', { fatal: true, ignoreBOM: true }).decode(
      bytes,
    );
  } catch {
    return Array.from(bytes, (byte) =>
      byte > 0x7f
        ? `%${byte.toString(16).toUpperCase()}`
        : String.fromCharCode(byte),
    ).join('');
  }
};

/**
 * The href a `data-mailto` value restores, or `null` for a value that is not
 * base64. An empty value is an editor's bare `mailto:` and restores as one.
 */
export const mailtoHref = (encoded: string): string | null => {
  try {
    return `mailto:${decodeAddress(encoded)}`;
  } catch {
    return null;
  }
};

const reported = new WeakSet<HTMLAnchorElement>();

/**
 * Restores the href of the mail links `TemplateUtils::create_link()` obfuscates,
 * moving the base64-encoded `data-mailto` address back onto the link. A value
 * that is not base64 stays in `data-mailto` with the href left at `#`: the
 * failure is reported once per link, and a click on it is cancelled so the page
 * does not jump to the top.
 */
const decodeLink = (link: HTMLAnchorElement, e: Event) => {
  const encoded = link.dataset.mailto;
  if (encoded === undefined) return;

  const href = mailtoHref(encoded);

  if (href === null) {
    if (e.type === 'click') e.preventDefault();

    if (!reported.has(link)) {
      reported.add(link);
      // eslint-disable-next-line no-console
      console.error(
        '[btc] data-mailto is not base64, the link stays inert',
        link,
      );
    }

    return;
  }

  link.href = href;
  delete link.dataset.mailto;
};

const getLink = (target: EventTarget | null) =>
  target instanceof Element
    ? target.closest<HTMLAnchorElement>('a[data-mailto]')
    : null;

const onInteraction = (e: Event) => {
  const link = getLink(e.target);
  if (link) decodeLink(link, e);
};

// Decode on first interaction: early enough for the click that follows, late
// enough that a crawler never triggers it. `click` covers a click with no
// pointerdown before it (`element.click()`, assistive tech): the anchor's
// activation behaviour reads the href after dispatch, so the href written
// here is the one the browser follows.
const EVENTS = ['pointerdown', 'keydown', 'focusin', 'click'] as const;

export default defineModule(
  () => {
    EVENTS.forEach((event) => document.addEventListener(event, onInteraction));
  },
  () => {
    EVENTS.forEach((event) =>
      document.removeEventListener(event, onInteraction),
    );
  },
);
